import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import {
  Box,
  IconButton,
  Stack,
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import LinkIcon from '@mui/icons-material/Link'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import AvatarSet from './AvatarSet'
import {
  FacebookConnectDataElement,
  InstagramConnectDataElement,
  NetworkType,
} from '@3m5/sm_publish-frontend-api'
import { NetworkType as ProfesSocicalNetworkType } from '@professocialApi/professocial-core-frontend-api'
import { useTranslation } from 'react-i18next'
import { PageOwner } from '@professocialApi/professocial-core-frontend-api/models'
import { SetUserAsPageMasterRequest } from '@professocialApi/professocial-core-frontend-api/apis/UserHasPageApi'
import { useDispatch, useSelector } from 'react-redux'
import { isUserFacebookPageConnected } from '../../../store/common/common.selectors'
import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'
import {
  FindByPnrRequest,
  AddUserPageRelationByAgencyNumberRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/UserSearchApi'
import {
  addUserPageRelationByAgencyNumberInitiated,
  getUserByPnrInitiated,
  resetPnrUser,
} from '../../../store/common/common.actions'
import { getPnrUser } from '../../../store/common/common.selectors'
import { CoreRootState } from '../../../store/coreRoot.reducer'
import SearchableDropDownComponent, { DropDownElement } from './SearchableDropDownComponent'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    marginTop: theme.spacing(3),
    padding: theme.spacing(5),
  },
  disabledWithTooltip: {
    pointerEvents: 'auto !important' as 'auto',
    '&:hover': { backgroundColor: 'transparent !important' },
  },
  connectButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: theme.palette.primary.main,
    },
  },
}))

type IProps = {
  page: FacebookConnectDataElement | InstagramConnectDataElement
  networkType: NetworkType
  disabled: boolean
  canAddPage?: boolean
  pageOwnersList?: PageOwner[]
  handleGetOwnerData?: (networkPageId: string) => void
  handleSetPageOwner?: (data: SetUserAsPageMasterRequest) => void
  connect?: (networkPageId: string) => void
  disconnect?: (networkPageId: string) => void
}

const ConnectPages: React.FC<IProps> = (props) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  // Define mobile/tablet as any screen below the "md" breakpoint.
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'))
  const dropdownWidth = isMobileOrTablet ? '100%' : '400px'

  const [selectedUserId, setSelectedUserId] = useState<string | undefined>()
  const [owners, setOwners] = useState<DropDownElement[]>([])
  const [searchedPNR, setSearchedPNR] = useState('')

  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorDialogMessage, setErrorDialogMessage] = useState('')

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('')
  const [pendingUser, setPendingUser] = useState<DropDownElement | null>(null)

  const pnrUser = useSelector(getPnrUser)
  const errors = useSelector((state: CoreRootState) => state.commonStore.error)
  const isPageConnectedWithCurrentUser = useSelector(
    isUserFacebookPageConnected(props.page.page.networkPageId)
  )

  const dispatchGetByPnr = useCallback(
    (params: FindByPnrRequest) => dispatch(getUserByPnrInitiated(params)),
    [dispatch]
  )
  const dispatchAddUserPageRelation = useCallback(
    (params: AddUserPageRelationByAgencyNumberRequest) =>
      dispatch(addUserPageRelationByAgencyNumberInitiated(params)),
    [dispatch]
  )

  useEffect(() => {
    if (props.handleGetOwnerData) {
      props.handleGetOwnerData(props.page.page.networkPageId)
    }
  }, [props.handleGetOwnerData, props.page.page.networkPageId])

  useEffect(() => {
    if (!pnrUser || !pnrUser.agencyNumber || !searchedPNR) return

    const agencyNumber = pnrUser.agencyNumber || ''
    const firstName = pnrUser.firstName || ''
    const lastName = pnrUser.lastName || ''
    const foundUser: DropDownElement = {
      key: pnrUser.agencyNumber,
      value: `${agencyNumber}-${firstName}-${lastName}`,
    }

    setPendingUser(foundUser)
    setConfirmDialogMessage(
      t('coreSettings:userFoundConfirm', {
        defaultValue: `Folgender Nutzer mit der PNR ${agencyNumber} wurde gefunden: ${firstName} ${lastName}.`,
      })
    )
    setShowConfirmDialog(true)
  }, [pnrUser, searchedPNR, t])

  const handleAcceptUser = () => {
    if (!pendingUser || !searchedPNR) return

    dispatchAddUserPageRelation({ pnr: searchedPNR })
    handleCloseConfirmDialog()

    // Fetch owners after request completes
    setTimeout(() => {
      if (props.handleGetOwnerData) {
        props.handleGetOwnerData(props.page.page.networkPageId)
      }
    }, 500)
  }

  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false)
    setPendingUser(null)
    dispatch(resetPnrUser())
    setSearchedPNR('')
  }

  useEffect(() => {
    if (!props.pageOwnersList) return

    let newSelectedId: string | undefined
    const mapped = props.pageOwnersList.map((owner) => {
      if (owner.isPageMaster) {
        newSelectedId = owner.userID
      }
      return {
        key: owner.userID,
        value: `${owner.vorname} ${owner.nachname}`,
      }
    })

    setOwners(mapped)
    setSelectedUserId((prev) => prev || newSelectedId)
  }, [props.pageOwnersList])

  const handleOwnerChange = (item: DropDownElement) => {
    setSelectedUserId(item.key)
    if (props.handleSetPageOwner) {
      props.handleSetPageOwner({
        userId: item.key,
        networkType: props.networkType as unknown as ProfesSocicalNetworkType,
        networkPageId: props.page.page.networkPageId,
      })
    }
  }

  /**
   * Handles the PNR search logic.
   * Returns 'fetch' if a fetch user request was dispatched,
   * 'link' if a link user request was dispatched.
   */
  const handleSearchPNR = (typedValue: string): 'fetch' | 'link' | undefined => {
    const [pnr] = typedValue.split('-')
    if (!pnr) return

    if (pnr === searchedPNR) {
      dispatchAddUserPageRelation({ pnr: searchedPNR })
      return 'link'
    } else {
      setSearchedPNR(pnr)
      dispatchGetByPnr({ pnr })
      return 'fetch'
    }
  }

  useEffect(() => {
    const userByPnrError = errors?.getUserByPnr as Response
    if (!userByPnrError || !searchedPNR) return

    if (userByPnrError.status === 404) {
      setErrorDialogMessage(
        t('coreSettings:noUserFoundWithPNR', {
          defaultValue: `Es konnte kein ERGO Mitarbeiter mit der PNR ${searchedPNR} gefunden werden.`,
          pnr: searchedPNR,
        })
      )
      setShowErrorDialog(true)
      resetToInitialState()
      dispatch(resetPnrUser())
    } else if (userByPnrError.status === 410) {
      userByPnrError.json().then((data) => {
        setErrorDialogMessage(
          t('coreSettings:userAlreadyConnectedToNetwork', {
            defaultValue: `Der Nutzer mit der PNR ${searchedPNR} ist bereits mit dieser ${props.networkType}-Seite verbunden.\nBitte wählen Sie "${data.firstName} ${data.lastName}" direkt aus der Liste.`,
          })
        )
      })
      setShowErrorDialog(true)
      resetToInitialState()
      dispatch(resetPnrUser())
    } else if (userByPnrError.status === 409) {
      setErrorDialogMessage(
        t('coreSettings:userAlreadyConnectedToNetwork', {
          defaultValue: `Der Nutzer mit der PNR ${searchedPNR} ist bereits mit einer anderen ${props.networkType}-Seite verbunden und kann nicht ausgewählt werden.`,
        })
      )
      setShowErrorDialog(true)
      resetToInitialState()
      dispatch(resetPnrUser())
    }
  }, [errors?.getUserByPnr, searchedPNR, dispatch, props.networkType, t])

  const resetToInitialState = () => {
    setSearchedPNR('')
    setSelectedUserId(undefined)
  }

  return (
    <>
      <Stack sx={{ px: 1, py: 2 }} spacing={4}>
        {/* Row containing the red button and the connected page */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            {isPageConnectedWithCurrentUser ? (
              <IconButton
                disabled={props.disabled}
                className={classes.connectButton}
                onClick={() =>
                  props.disconnect && props.disconnect(props.page.page.networkPageId)
                }
              >
                <LinkOffIcon />
              </IconButton>
            ) : (
              <>
                {!props.canAddPage ? (
                  <TooltipComponent
                    title={t('coreSettings:connectionDisabled')}
                    type="html"
                    child={
                      <IconButton
                        disabled={props.disabled}
                        className={`${classes.connectButton} ${classes.disabledWithTooltip}`}
                        onClick={() =>
                          props.connect && props.connect(props.page.page.networkPageId)
                        }
                      >
                        <LinkIcon />
                      </IconButton>
                    }
                    placement="top"
                    arrow
                  />
                ) : (
                  <IconButton
                    disabled={props.disabled}
                    className={classes.connectButton}
                    onClick={() =>
                      props.connect && props.connect(props.page.page.networkPageId)
                    }
                  >
                    <LinkIcon />
                  </IconButton>
                )}
              </>
            )}
          </Box>

          <AvatarSet page={props.page} networkType={props.networkType as unknown as ProfesSocicalNetworkType} />

          {/* On larger screens, render the dropdown in the same row */}
          {!isMobileOrTablet && props.page.isConnected && owners.length > 0 && (
            <Box sx={{ width: dropdownWidth }}>
              <SearchableDropDownComponent
                size="small"
                label={t('coreSettings:siteOwner')}
                options={owners}
                value={selectedUserId}
                onChange={handleOwnerChange}
                onSearch={handleSearchPNR}
                disableClearable
              />
            </Box>
          )}
        </Stack>

        {/* On mobile/tablet, render the dropdown on a new row */}
        {isMobileOrTablet && props.page.isConnected && owners.length > 0 && (
          <Box sx={{ width: dropdownWidth, alignSelf: 'center' }}>
            <SearchableDropDownComponent
              size="small"
              label={t('coreSettings:siteOwner')}
              options={owners}
              value={selectedUserId}
              onChange={handleOwnerChange}
              onSearch={handleSearchPNR}
              disableClearable
            />
          </Box>
        )}
      </Stack>

      <Dialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' || event.key === 'Enter') {
            setShowErrorDialog(false)
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          {t('coreSettings:errorDialogTitle', 'Hinweis')}
          <br />
        </DialogTitle>
        <DialogContent>{errorDialogMessage}</DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            onClick={() => setShowErrorDialog(false)}
            color="primary"
            variant="contained"
          >
            {t('coreSettings:ok', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            handleCloseConfirmDialog()
          } else if (event.key === 'Enter') {
            handleAcceptUser()
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          {t('coreSettings:confirmDialogTitle', 'Nutzer gefunden')}
        </DialogTitle>
        <DialogContent>
          {confirmDialogMessage}
          <br />
          <br />
          Jetzt als Fanpageinhaber bestätigen.
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={handleCloseConfirmDialog} color="secondary">
            {t('coreSettings:cancel', 'Abbrechen')}
          </Button>
          <Button onClick={handleAcceptUser} color="primary" variant="contained">
            {t('coreSettings:confirm', 'Bestätigen')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ConnectPages
